import { getCompanyNameError, getEmailError } from "@shared/utils/validation";
import { httpsCallable } from "firebase/functions";
import { useEffect, useState, type FormEvent } from "react";
import { LocationAutocompleteField } from "../../form/LocationAutocompleteField";
import { functions } from "../../../config/firebase";
import { SYSTEM_EMAIL } from "@shared/constants/app";
import { useCreateCompany } from "../../../hooks/useCreateCompany";
import { ErrorMessage } from "../../ErrorMessage";
import { ModalButtonGroup } from "../../common/ModalButtonGroup";
import { SELECT_CHEVRON_STYLE } from "../../../styles/components";

interface CreateCompanyModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

// Future enhancement: Move country codes to a separate config file for better maintainability and reuse across the app (UserProfileForm.tsx, etc.)
const COUNTRY_CODES = [
  { code: "+353", flag: "🇮🇪", country: "Ireland" },
  { code: "+44", flag: "🇬🇧", country: "United Kingdom" },
  { code: "+1", flag: "🇺🇸", country: "United States" },
  { code: "+33", flag: "🇫🇷", country: "France" },
  { code: "+49", flag: "🇩🇪", country: "Germany" },
  { code: "+39", flag: "🇮🇹", country: "Italy" },
  { code: "+34", flag: "🇪🇸", country: "Spain" },
  { code: "+31", flag: "🇳🇱", country: "Netherlands" },
  { code: "+32", flag: "🇧🇪", country: "Belgium" },
  { code: "+46", flag: "🇸🇪", country: "Sweden" },
  { code: "+47", flag: "🇳🇴", country: "Norway" },
  { code: "+45", flag: "🇩🇰", country: "Denmark" },
  { code: "+358", flag: "🇫🇮", country: "Finland" },
  { code: "+41", flag: "🇨🇭", country: "Switzerland" },
  { code: "+43", flag: "🇦🇹", country: "Austria" },
  { code: "+48", flag: "🇵🇱", country: "Poland" },
  { code: "+420", flag: "🇨🇿", country: "Czech Republic" },
  { code: "+36", flag: "🇭🇺", country: "Hungary" },
  { code: "+30", flag: "🇬🇷", country: "Greece" },
  { code: "+351", flag: "🇵🇹", country: "Portugal" },
  { code: "+61", flag: "🇦🇺", country: "Australia" },
  { code: "+64", flag: "🇳🇿", country: "New Zealand" },
  { code: "+81", flag: "🇯🇵", country: "Japan" },
  { code: "+82", flag: "🇰🇷", country: "South Korea" },
  { code: "+86", flag: "🇨🇳", country: "China" },
  { code: "+91", flag: "🇮🇳", country: "India" },
  { code: "+55", flag: "🇧🇷", country: "Brazil" },
  { code: "+52", flag: "🇲🇽", country: "Mexico" },
];

export function CreateCompanyModal({
  onClose,
  onSuccess,
}: CreateCompanyModalProps) {
  // Company details
  const [companyName, setCompanyName] = useState("");
  const [companyEmail, setCompanyEmail] = useState("");
  const [companyPhoneCode, setCompanyPhoneCode] = useState("+353");
  const [companyPhoneNumber, setCompanyPhoneNumber] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");

  // Administrator details
  const [adminFirstName, setAdminFirstName] = useState("");
  const [adminLastName, setAdminLastName] = useState("");
  const [adminPhoneCode, setAdminPhoneCode] = useState("+353");
  const [adminPhoneNumber, setAdminPhoneNumber] = useState("");

  // State
  const [showSuccess, setShowSuccess] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [locationKey, setLocationKey] = useState(0);

  const { createCompanyWithAdmin, loading, error, resetLoading } = useCreateCompany();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Trim all inputs
    const trimmedCompanyName = companyName.trim();
    const trimmedCompanyEmail = companyEmail.trim();
    const trimmedCompanyPhone = companyPhoneNumber.trim()
      ? `${companyPhoneCode} ${companyPhoneNumber.trim()}`
      : "";
    const trimmedCompanyAddress = companyAddress.trim();
    const trimmedAdminFirstName = adminFirstName.trim();
    const trimmedAdminLastName = adminLastName.trim();
    const trimmedAdminPhone = adminPhoneNumber.trim()
      ? `${adminPhoneCode} ${adminPhoneNumber.trim()}`
      : "";

    // Validate company name
    const nameError = getCompanyNameError(trimmedCompanyName);
    if (nameError) {
      setEmailError(nameError);
      return;
    }

    // Validate admin email
    const emailValidationError = getEmailError(trimmedCompanyEmail);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      return;
    }

    // Validate inputs
    if (!trimmedAdminFirstName || !trimmedAdminLastName) {
      setEmailError("Please fill in all required fields");
      return;
    }

    setEmailError(""); // Clear validation errors before proceeding
    setSubmitting(true);

    try {
      const result = await createCompanyWithAdmin({
        companyName: trimmedCompanyName,
        companyEmail: trimmedCompanyEmail,
        companyPhone: trimmedCompanyPhone,
        companyAddress: trimmedCompanyAddress,
        adminFirstName: trimmedAdminFirstName,
        adminLastName: trimmedAdminLastName,
        adminPhone: trimmedAdminPhone,
      });

      if (result.success && result.password) {
        setGeneratedPassword(result.password);

        // Send invitation email to administrator
        setEmailError("");
        try {
          const sendInvitationEmail = httpsCallable(
            functions,
            "sendInvitationEmail",
          );
          await sendInvitationEmail({
            toEmail: companyEmail,
            toFirstName: adminFirstName,
            temporaryPassword: result.password,
            fromEmail: SYSTEM_EMAIL.ADDRESS,
            fromName: SYSTEM_EMAIL.NAME,
            companyName: companyName,
            userRole: "Company Administrator",
          });
        } catch (err) {
          console.error("Failed to send administrator invitation email:", err);
          setEmailError(
            "Company created but failed to send invitation email. Please contact the company administrator directly with their login credentials.",
          );
          resetLoading();
        }

        setShowSuccess(true);
      }
    } finally {
      setSubmitting(false);
    }
  };



  const resetForm = () => {
    setCompanyName('');
    setCompanyEmail('');
    setCompanyPhoneCode('+353');
    setCompanyPhoneNumber('');
    setCompanyAddress('');
    setAdminFirstName('');
    setAdminLastName('');
    setAdminPhoneCode('+353');
    setAdminPhoneNumber('');
    setShowSuccess(false);
    setEmailError('');
    setGeneratedPassword('');
    setLocationKey((k) => k + 1);
  };

  const handleClose = () => {
    const wasSuccess = showSuccess;
    resetForm();
    onClose();
    if (wasSuccess) {
      onSuccess();
    }
  };

  // Accessibility: Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !loading) {
        handleClose();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [loading]);

  if (showSuccess) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="success-modal-title"
        style={overlayStyle}
      >
        <div style={modalStyle}>
          <h2
            id="success-modal-title"
            style={{
              fontSize: "24px",
              fontWeight: "700",
              marginBottom: "16px",
              color: "#111827",
            }}
          >
            Company Created Successfully!
          </h2>

          <div style={{ marginBottom: "24px" }}>
            <p
              style={{
                fontSize: "16px",
                color: "#374151",
                marginBottom: "16px",
              }}
            >
              <strong>Company:</strong> {companyName}
              <br />
              <strong>Administrator:</strong> {adminFirstName} {adminLastName}
              <br />
              <strong>Login Email:</strong> {companyEmail}
            </p>

            {emailError ? (
              <div
                style={{
                  backgroundColor: "#fef3c7",
                  border: "1px solid #fbbf24",
                  borderRadius: "8px",
                  padding: "16px",
                  marginBottom: "16px",
                }}
              >
                <p
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#92400e",
                    marginBottom: "8px",
                  }}
                >
                  ⚠️ Email Failed
                </p>
                <p style={{ fontSize: "13px", color: "#78350f" }}>
                  {emailError}
                </p>
                <div
                  style={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    padding: "12px",
                    marginTop: "12px",
                  }}
                >
                  <p
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#374151",
                      marginBottom: "4px",
                    }}
                  >
                    Temporary Password:
                  </p>
                  <code
                    style={{
                      fontSize: "14px",
                      color: "#111827",
                      fontFamily: "monospace",
                    }}
                  >
                    {generatedPassword}
                  </code>
                </div>
              </div>
            ) : (
              <div
                style={{
                  backgroundColor: "#d1fae5",
                  border: "1px solid #10b981",
                  borderRadius: "8px",
                  padding: "16px",
                  marginBottom: "16px",
                }}
              >
                <p
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#065f46",
                    marginBottom: "8px",
                  }}
                >
                  ✓ Invitation Email Sent!
                </p>
                <p style={{ fontSize: "13px", color: "#047857" }}>
                  An email with login credentials has been automatically sent to{" "}
                  <strong>{companyEmail}</strong>. The administrator can use
                  these credentials to log in and will be prompted to change
                  their password on first login.
                </p>
              </div>
            )}
          </div>

          <ModalButtonGroup onCancel={handleClose} hideCancel submitLabel="Close" submitType="button" onSubmit={handleClose} />
        </div>
      </div>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-modal-title"
      style={overlayStyle}
    >
      <div style={modalStyle}>
        <h2
          id="create-modal-title"
          style={{
            fontSize: "24px",
            fontWeight: "700",
            margin: "0 0 24px 0",
            color: "#111827",
          }}
        >
          Create New Company
        </h2>

        <form onSubmit={handleSubmit}>
          {/* Company Details */}
          <div style={{ marginBottom: "24px" }}>
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "16px",
                color: "#374151",
              }}
            >
              Company Details
            </h3>

            <div style={formGroupStyle}>
              <label style={labelStyle}>
                Company Name <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
                disabled={loading}
                style={inputStyle}
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>
                Company Email <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                type="email"
                value={companyEmail}
                onChange={(e) => setCompanyEmail(e.target.value)}
                required
                disabled={loading}
                placeholder="company@example.com"
                style={inputStyle}
              />
              <p
                style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}
              >
                This email will be used for administrator authentication
              </p>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Company Phone</label>
              <div style={{ display: "flex", gap: "8px" }}>
                <select
                  value={companyPhoneCode}
                  onChange={(e) => setCompanyPhoneCode(e.target.value)}
                  disabled={loading}
                  style={{ ...inputStyle, ...SELECT_CHEVRON_STYLE, width: "140px", flexShrink: 0, paddingRight: "36px" }}
                >
                  {COUNTRY_CODES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.flag} {c.code}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  value={companyPhoneNumber}
                  onChange={(e) =>
                    setCompanyPhoneNumber(
                      e.target.value.replace(/[^\d\s\-().]/g, ""),
                    )
                  }
                  placeholder="1 234 5678"
                  disabled={loading}
                  style={{ ...inputStyle, flex: 1 }}
                />
              </div>
            </div>

            <div style={formGroupStyle}>
              <LocationAutocompleteField
                key={locationKey}
                label="Company Address"
                onChange={(result) => setCompanyAddress(result.address)}
              />
            </div>
          </div>

          {/* Administrator Details */}
          <div style={{ marginBottom: "24px" }}>
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "16px",
                color: "#374151",
              }}
            >
              Administrator Information
            </h3>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
                marginBottom: "16px",
              }}
            >
              <div>
                <label style={labelStyle}>
                  First Name <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="text"
                  value={adminFirstName}
                  onChange={(e) => setAdminFirstName(e.target.value)}
                  required
                  disabled={loading}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  Last Name <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="text"
                  value={adminLastName}
                  onChange={(e) => setAdminLastName(e.target.value)}
                  required
                  disabled={loading}
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Admin Phone</label>
              <div style={{ display: "flex", gap: "8px" }}>
                <select
                  value={adminPhoneCode}
                  onChange={(e) => setAdminPhoneCode(e.target.value)}
                  disabled={loading}
                  style={{ ...inputStyle, ...SELECT_CHEVRON_STYLE, width: "140px", flexShrink: 0, paddingRight: "36px" }}
                >
                  {COUNTRY_CODES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.flag} {c.code}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  value={adminPhoneNumber}
                  onChange={(e) =>
                    setAdminPhoneNumber(
                      e.target.value.replace(/[^\d\s\-().]/g, ""),
                    )
                  }
                  placeholder="86 123 4567"
                  disabled={loading}
                  style={{ ...inputStyle, flex: 1 }}
                />
              </div>
            </div>
          </div>

          {(error || emailError) && (
            <div style={{ marginBottom: "24px" }}>
              <ErrorMessage
                message={error || emailError}
                onDismiss={() => setEmailError("")}
              />
            </div>
          )}

          <ModalButtonGroup
            onCancel={handleClose}
            submitLabel="Create Company"
            isLoading={submitting}
            loadingLabel="Creating..."
          />
        </form>
      </div>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  borderRadius: "12px",
  padding: "32px",
  maxWidth: "600px",
  width: "90%",
  maxHeight: "90vh",
  overflowY: "auto",
  boxShadow:
    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  boxSizing: "border-box",
};

const formGroupStyle: React.CSSProperties = {
  marginBottom: "16px",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: "14px",
  fontWeight: "500",
  color: "#374151",
  marginBottom: "6px",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  fontSize: "14px",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  outline: "none",
  boxSizing: "border-box",
};

